import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Detecta erros de carregamento de módulos dinâmicos (chunks).
 * Isso acontece quando o service worker ou cache serve um index.html antigo
 * que referencia chunks JS que não existem mais após um novo deploy.
 */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message?.toLowerCase() ?? '';
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('dynamically imported module')
  );
}

const RELOAD_KEY = 'bolao_chunk_reload';

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);

    // Auto-reload on chunk load errors (stale cache after deploy)
    // Use sessionStorage flag to prevent infinite reload loops
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();

      // Only auto-reload if we haven't reloaded in the last 10 seconds
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
        return;
      }
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleLogout = (): void => {
    localStorage.removeItem('bolao_token');
    localStorage.removeItem('bolao_refresh_token');
    localStorage.removeItem('bolao_user');
    localStorage.removeItem('bolao_active_bolao');
    window.location.href = '/login';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const isChunkError = this.state.error ? isChunkLoadError(this.state.error) : false;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '24px',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <span style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</span>
          <h1 style={{ fontSize: '20px', marginBottom: '8px' }}>
            {isChunkError ? 'Atualização disponível' : 'Algo deu errado'}
          </h1>
          <p style={{ color: '#666', marginBottom: '24px', maxWidth: '320px' }}>
            {isChunkError
              ? 'Uma nova versão do app está disponível. Clique abaixo para atualizar.'
              : (this.state.error?.message ?? 'Erro inesperado no aplicativo.')}
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: isChunkError ? 'none' : '1px solid #ccc',
                background: isChunkError ? '#1B5E20' : '#fff',
                color: isChunkError ? '#fff' : 'inherit',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {isChunkError ? 'Atualizar agora' : 'Tentar novamente'}
            </button>
            {!isChunkError && (
              <button
                type="button"
                onClick={this.handleLogout}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#1B5E20',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Fazer login
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
